import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { InputWrapperComponent } from '../../../../shared/components/input/input-wrapper/input-wrapper.component';

type StatisticsPeriod = 'yearly' | 'monthly' | 'daily';

@Component({
  selector: 'app-statistics-period-input',
  imports: [CommonModule, IconComponent, InputWrapperComponent],
  templateUrl: './statistics-period-input.component.html',
  styleUrl: './statistics-period-input.component.scss',
})
export class StatisticsPeriodInputComponent {
  @Input({ required: true }) control!: AbstractControl;
  @Input({ required: true }) period!: StatisticsPeriod;
  @Input() label?: string;

  readonly years = this.buildYearOptions();
  readonly today = new Date();

  get requiredMarker(): string {
    if (!this.control.hasValidator(Validators.required)) return '';
    return '*';
  }

  get isDaily(): boolean {
    return this.period === 'daily';
  }

  get isMonthly(): boolean {
    return this.period === 'monthly';
  }

  get isYearly(): boolean {
    return this.period === 'yearly';
  }

  get dailyValue(): string {
    return this.toDateInputValue(this.control.value);
  }

  get monthlyValue(): string {
    return this.toMonthInputValue(this.control.value);
  }

  get yearlyValue(): number | '' {
    const date = this.control.value as Date | null;
    return date instanceof Date && !Number.isNaN(date.getTime())
      ? date.getFullYear()
      : '';
  }

  get maxDailyValue(): string {
    return this.toDateInputValue(this.today);
  }

  get maxMonthlyValue(): string {
    return this.toMonthInputValue(this.today);
  }

  get canSelectNext(): boolean {
    return !this.isFutureSelection(this.shiftDate(this.getCurrentDate(), 1));
  }

  onDailyChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const date = value ? this.parseDateValue(value) : null;
    if (date && this.isFutureDate(date)) {
      return;
    }
    this.setValue(date);
  }

  onMonthlyChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const date = value ? this.parseMonthValue(value) : null;
    if (date && this.isFutureMonth(date)) {
      return;
    }
    this.setValue(date);
  }

  onYearChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    const date = value ? this.parseYearValue(value) : null;
    if (date && this.isFutureYear(date)) {
      return;
    }
    this.setValue(date);
  }

  private setValue(value: Date | null) {
    this.control.setValue(value);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  private buildYearOptions(range = 11): number[] {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: range }, (_, index) => currentYear - index);
  }

  private toDateInputValue(value: unknown): string {
    const date = value instanceof Date ? value : null;

    if (!date || Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private toMonthInputValue(value: unknown): string {
    const date = value instanceof Date ? value : null;

    if (!date || Number.isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');

    return `${year}-${month}`;
  }

  private parseDateValue(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private parseMonthValue(value: string): Date {
    const [year, month] = value.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }

  private parseYearValue(value: string): Date {
    return new Date(Number(value), 0, 1);
  }

  private isFutureDate(date: Date): boolean {
    const today = new Date();
    const current = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const selected = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    return selected > current;
  }

  private isFutureMonth(date: Date): boolean {
    const today = new Date();
    const current = new Date(today.getFullYear(), today.getMonth(), 1);
    const selected = new Date(date.getFullYear(), date.getMonth(), 1);
    return selected > current;
  }

  private isFutureYear(date: Date): boolean {
    return date.getFullYear() > new Date().getFullYear();
  }

  onSelectPrev = () => {
    this.setValue(this.shiftDate(this.getCurrentDate(), -1));
  };

  onSelectNext = () => {
    const next = this.shiftDate(this.getCurrentDate(), 1);

    if (this.isFutureSelection(next)) {
      return;
    }

    this.setValue(next);
  };

  private getCurrentDate(): Date {
    const value = this.control.value as Date | null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    return this.getTodayStart();
  }

  private shiftDate(date: Date, direction: number): Date {
    const next = new Date(date);

    switch (this.period) {
      case 'daily':
        next.setDate(next.getDate() + direction);
        return next;
      case 'monthly':
        next.setDate(1);
        next.setMonth(next.getMonth() + direction);
        return next;
      case 'yearly':
      default:
        next.setMonth(0, 1);
        next.setFullYear(next.getFullYear() + direction);
        return next;
    }
  }

  private isFutureSelection(date: Date): boolean {
    const today = this.getTodayStart();

    switch (this.period) {
      case 'daily':
        return date > today;
      case 'monthly':
        return (
          new Date(date.getFullYear(), date.getMonth(), 1) >
          new Date(today.getFullYear(), today.getMonth(), 1)
        );
      case 'yearly':
      default:
        return date.getFullYear() > today.getFullYear();
    }
  }

  private getTodayStart(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
}
